/* cspell:word prerenders */
import {round} from 'ramda-adjunct'
import {notifyBugsnag} from '~/utils/Bugsnag/notify'
import {toEnglishList} from '~/utils/toEnglishList'
import {safeUnreachableCase} from '~/utils/unreachableCase'
import {type SignupOffer} from '~/utils/useSanityReferrerData/types'

type ISpendRewardOffer = {
  spendRequirementThousands: number
  rewardAmount: number
}

export const getSpendAmountRewardOffer = (offer: ISpendRewardOffer) =>
  `Spend $${offer.spendRequirementThousands}K on your debit or credit card within 90 days to get $${offer.rewardAmount}.`

const thousandsAbbreviator = (amount: number) => {
  const dollars = round(amount)

  if (dollars >= 1000) {
    return `$${dollars / 1000.0}K`
  } else {
    return `$${dollars}`
  }
}

type TReward =
  | TReward409a
  | TRewardTeaRoom
  | TRewardAWS5k
  | TRewardCashBonus
  | TRewardCreditLimitIncrease

type TReward409a = {
  kind: '409a'
}

type TRewardTeaRoom = {
  kind: 'teaRoom'
}

type TRewardAWS5k = {
  kind: 'aWS5k'
}

type TRewardCashBonus = {
  kind: 'cashBonus'
  amount: number
}

type TRewardCreditLimitIncrease = {
  kind: 'creditLimitIncrease'
}

type TRequirement = TRequirementDeposit | TRequirementDebitSpend

type TRequirementDeposit = {
  kind: 'deposit'
  amount: number
}

type TRequirementDebitSpend = {
  kind: 'debitSpend'
  amount: number
}

// A pre-render representation of the offer
// which we can use for both rendering the offer
// text and a performing a few checks to make sure
// the offer text makes sense when rendered in
// combination with others.
type TOffer = {
  reward: TReward
  requirements: TRequirement[]
  timeLimit: number | undefined
  exclusive: boolean
}

const renderReward = (reward: TReward): string => {
  switch (reward.kind) {
    case '409a':
      return 'a free 409a valuation worth $2K'
    case 'teaRoom':
      return 'access to the Tea Room'
    case 'aWS5k':
      return '$5K in AWS credits'
    case 'cashBonus':
      return `${thousandsAbbreviator(reward.amount)} cash`
    case 'creditLimitIncrease':
      return 'increase in credit limit'
    default:
      return safeUnreachableCase(reward, '')
  }
}

const renderRequirement = (requirement: TRequirement): string => {
  switch (requirement.kind) {
    case 'debitSpend':
      return `spend ${thousandsAbbreviator(
        requirement.amount
      )} on your debit or credit card`
    case 'deposit':
      return `deposit ${thousandsAbbreviator(requirement.amount)}`
    default:
      return safeUnreachableCase(requirement, '')
  }
}

type TOfferPrerender = {
  rewardText: string
  requirementText: string
}

const prerenderOffer = (offer: TOffer): TOfferPrerender => {
  const {reward, requirements, timeLimit} = offer

  const rewardText = renderReward(reward)
  const requirementText =
    requirements.length > 0
      ? toEnglishList(requirements.map(renderRequirement)) +
        ' ' +
        (timeLimit ? `within your first ${offer.timeLimit || 'few'} days` : '')
      : 'start using Mercury'

  return {rewardText, requirementText}
}

const eligibleOffer = (offer: SignupOffer): boolean => {
  return ['PartnerPage', undefined].includes(offer.referralMethodRequirement)
}

const preprocessOffer = (offer: SignupOffer): TOffer => {
  const reward: TReward = (() => {
    if (offer.kind === '') {
      notifyBugsnag('Offer kind is an empty string')
      throw new Error('Offer is empty')
    }

    if (offer.kind === 'cashBonus') {
      const cashReward: TRewardCashBonus = {
        kind: offer.kind,
        amount: offer.cashBonusAmount || 0,
      }

      return cashReward
    }

    return {kind: offer.kind}
  })()

  const requirements: TRequirement[] = (() => {
    const requirementsList: TRequirement[] = []

    if (offer.depositRequirement) {
      requirementsList.push({
        kind: 'deposit',
        amount: offer.depositRequirement,
      })
    }

    if (offer.debitSpendRequirement) {
      requirementsList.push({
        kind: 'debitSpend',
        amount: offer.debitSpendRequirement,
      })
    }

    return requirementsList
  })()

  return {
    reward,
    requirements,
    timeLimit: offer.cutoffDays,
    exclusive: offer.exclusiveWithOtherDeals,
  }
}

export const generateOfferBanner = (
  rawOffers: SignupOffer[]
): string | undefined => {
  if (rawOffers.length === 0) {
    return undefined
  }

  // We enforce a maximum of two offers per page.
  const offers = rawOffers.filter(eligibleOffer).slice(0, 2).map(preprocessOffer)
  const prerenders = offers.map(prerenderOffer)
  if (offers.length === 1) {
    return (
      [
        'When you',
        prerenders[0].requirementText + ',',
        'get',
        prerenders[0].rewardText,
      ].join(' ') + '.'
    )
  }

  const [offer1, offer2] = offers
  const compositor = offers[0].exclusive || offers[1].exclusive ? 'or' : 'and'
  const redundantRequirements =
    prerenders[0].requirementText === prerenders[1].requirementText
  if (redundantRequirements) {
    return (
      [
        'When you',
        prerenders[0].requirementText + ',',
        'get',
        prerenders[0].rewardText,
        compositor,
        prerenders[1].rewardText,
      ].join(' ') + '.'
    )
  }

  const conjunction =
    compositor +
    (offer1.reward.kind === offer2.reward.kind && compositor === 'and'
      ? ' another'
      : '')
  return (
    [
      'Get',
      prerenders[0].rewardText,
      'when you',
      prerenders[0].requirementText + ',',
      conjunction,
      prerenders[1].rewardText,
      'when you',
      prerenders[1].requirementText,
    ].join(' ') + '.'
  )
}
